import { useState, useEffect, createContext, useContext, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../lib/api";

// Auth Context
const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const authCheckRef = useRef(false);

  const checkAuth = useCallback(async () => {
    // Check if token exists first
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return null;
    }

    try {
      const response = await api.get(`/auth/me`);
      setUser(response.data);
      return response.data;
    } catch (error) {
      // Only clear token on explicit 401 unauthorized
      if (error.response?.status === 401) {
        localStorage.removeItem('auth_token');
        setUser(null);
      } else {
        // For network errors, keep token and try again later
        console.warn('Auth check failed with network error, keeping token');
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Prevent double auth check
    if (authCheckRef.current) return;
    authCheckRef.current = true;

    // If user passed from login page, use it directly
    if (location.state?.user) {
      setUser(location.state.user);
      setLoading(false);
      // Clear the state
      window.history.replaceState({}, document.title);
      return;
    }

    // Check authentication
    checkAuth();
  }, [location.state?.user, checkAuth]);

  const logout = async () => {
    try {
      await api.post(`/auth/logout`);
    } catch (error) {
      console.error("Logout error:", error);
    }
    // Clear token from localStorage
    localStorage.removeItem('auth_token');
    setUser(null);
    navigate("/login");
  };

  const refreshUser = useCallback(async () => {
    setLoading(true);
    return await checkAuth();
  }, [checkAuth]);

  return (
    <AuthContext.Provider value={{ user, loading, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export default function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login", { replace: true });
    }
  }, [loading, user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen scene-emerald flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-gold border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-luxury">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return children;
}
